import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { CatalogProvider } from "@/context/CatalogContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NewsletterBlock from "@/components/NewsletterBlock";
import CartDrawer from "@/components/CartDrawer";
import AuthModal from "@/components/AuthModal";
import MaintenanceOverlay from "@/components/MaintenanceOverlay";
import Home from "@/pages/Home";
import Collection from "@/pages/Collection";
import PDP from "@/pages/PDP";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import OrderStatus from "@/pages/OrderStatus";
import About from "@/pages/About";
import Journal from "@/pages/Journal";
import Account from "@/pages/Account";
import Help from "@/pages/Help";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import Legal from "@/pages/Legal";
import ResetPassword from "@/pages/ResetPassword";
import JournalPost from "@/pages/JournalPost";
import CookieBanner from "@/components/CookieBanner";
import ScrollToTop from "@/components/ScrollToTop";
import AnnouncementBar from "@/components/AnnouncementBar";
import { useSEO } from "@/hooks/useSEO";
import useVisitorTracker from "@/hooks/useVisitorTracker";
import { TESTIDS } from "@/constants/testIds";

const Shell = () => {
  const [authOpen, setAuthOpen] = useState(false);
  const location = useLocation();
  const isPdp = location.pathname.startsWith("/product/");
  const isAdmin = location.pathname.startsWith("/admin");

  // Default per-route SEO. Pages that need custom metadata (e.g. PDP)
  // call useSEO() again with an override — the last call wins.
  useSEO();
  // Anonymous self-hosted traffic analytics — fires on every route change.
  // The hook itself no-ops on /admin/* so admin visits don't skew stats.
  useVisitorTracker();

  // Emergent Google Auth returns to /account#session_id=... — detect the
  // fragment SYNCHRONOUSLY during render (not in useEffect) so we short-circuit
  // before AuthContext's /me check races the callback.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // Admin routes render bare (no site chrome, no cart context UI)
  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/*" element={<Admin />} />
      </Routes>
    );
  }

  return (
    <div className="App">
      <ScrollToTop />
      <AnnouncementBar />
      <Header onOpenAuth={() => setAuthOpen(true)} />
      <main className={isPdp ? "" : ""}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/collection/:category" element={<Collection />} />
          <Route path="/collection" element={<Navigate to="/collection/all" replace />} />
          <Route path="/product/:slug" element={<PDP onRequireAuth={() => setAuthOpen(true)} />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/order/status/:sessionId" element={<OrderStatus />} />
          <Route path="/about" element={<About />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:slug" element={<JournalPost />} />
          <Route path="/help/:topic" element={<Help />} />
          <Route path="/legal/:topic" element={<Legal />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account" element={<Account />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <NewsletterBlock />
      <Footer />
      <CartDrawer onRequireAuth={() => setAuthOpen(true)} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <CookieBanner />
      <MaintenanceOverlay />
    </div>
  );
};

import SiteGate from "@/components/SiteGate";

function App() {
  return (
    <BrowserRouter>
      <SiteContentProvider>
        <LanguageProvider>
          <SiteGate>
            <AuthProvider>
              <CartProvider>
                <CatalogProvider>
                  <Shell />
                </CatalogProvider>
              </CartProvider>
            </AuthProvider>
          </SiteGate>
        </LanguageProvider>
      </SiteContentProvider>
    </BrowserRouter>
  );
}

export default App;
